import type { FC, ReactNode } from 'react'

import { createElement, useRef } from 'react'
import { cn } from '@/lib/utils'
import { gsap, useGSAP, MOTION_QUERY } from '@/lib/gsap'

interface ScrubTextProps {
    text: string
    as?: 'p' | 'h2' | 'blockquote'
    className?: string
    accent?: string[]
    accentClassName?: string
}

/**
 * Statement text whose words light up one by one as the reader scrolls past it.
 */
export const ScrubText: FC<ScrubTextProps> = ({
    text,
    as = 'p',
    className,
    accent = [],
    accentClassName = 'text-ember'
}): ReactNode => {
    const ref = useRef<HTMLElement>(null)
    const words = text.split(' ')

    useGSAP(() => {
        const element = ref.current

        if (!element) return

        const media = gsap.matchMedia()

        media.add(MOTION_QUERY, () => {
            gsap.fromTo(
                gsap.utils.toArray<HTMLElement>('[data-word]', element),
                { opacity: 0.14 },
                {
                    opacity: 1,
                    ease: 'none',
                    stagger: 0.12,
                    scrollTrigger: { trigger: element, start: 'top 82%', end: 'bottom 48%', scrub: 0.5 }
                }
            )
        })

        return () => media.revert()
    }, { scope: ref })

    const content = words.map((word, index) => (
        <span key={`${word}-${index}`}>
            <span data-word className={cn(accent.includes(word) && accentClassName)}>{word}</span>
            {index < words.length - 1 && ' '}
        </span>
    ))

    return createElement(as, { ref, className }, content)
}
